import { createStyles } from 'antd-style'
import { useCallback, useEffect, useRef } from 'react'

const FADE_SIZE = 20
const FADE_EDGE = 'rgba(0, 0, 0, 0.35)'

const useFadeStyles = createStyles(() => ({
  fade: {
    '&[data-fade-top]': {
      WebkitMaskImage: `linear-gradient(to bottom, ${FADE_EDGE}, #000 ${FADE_SIZE}px)`,
      maskImage: `linear-gradient(to bottom, ${FADE_EDGE}, #000 ${FADE_SIZE}px)`
    },
    '&[data-fade-bottom]': {
      WebkitMaskImage: `linear-gradient(to top, ${FADE_EDGE}, #000 ${FADE_SIZE}px)`,
      maskImage: `linear-gradient(to top, ${FADE_EDGE}, #000 ${FADE_SIZE}px)`
    },
    '&[data-fade-top][data-fade-bottom]': {
      WebkitMaskImage: `linear-gradient(to bottom, ${FADE_EDGE}, #000 ${FADE_SIZE}px, #000 calc(100% - ${FADE_SIZE}px), ${FADE_EDGE})`,
      maskImage: `linear-gradient(to bottom, ${FADE_EDGE}, #000 ${FADE_SIZE}px, #000 calc(100% - ${FADE_SIZE}px), ${FADE_EDGE})`
    }
  }
}))

export const useScrollFade = () => {
  const { styles } = useFadeStyles()
  const nodeRef = useRef<HTMLElement | null>(null)
  const reversedRef = useRef(false)
  const frameRef = useRef<number | undefined>(undefined)
  const resizeRef = useRef<ResizeObserver | null>(null)
  const mutationRef = useRef<MutationObserver | null>(null)

  const refresh = useCallback(() => {
    const node = nodeRef.current
    if (!node) return

    const max = node.scrollHeight - node.clientHeight
    const dist = Math.abs(node.scrollTop)
    const reversed = reversedRef.current
    node.toggleAttribute('data-fade-top', reversed ? dist < max - 1 : dist > 1)
    node.toggleAttribute(
      'data-fade-bottom',
      reversed ? dist > 1 : dist < max - 1
    )
  }, [])

  const schedule = useCallback(() => {
    if (frameRef.current !== undefined) return

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = undefined
      refresh()
    })
  }, [refresh])

  const detach = useCallback(() => {
    nodeRef.current?.removeEventListener('scroll', schedule)
    resizeRef.current?.disconnect()
    mutationRef.current?.disconnect()
    resizeRef.current = null
    mutationRef.current = null

    if (frameRef.current !== undefined) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = undefined
    }
  }, [schedule])

  const ref = useCallback(
    (node: HTMLElement | null) => {
      if (node === nodeRef.current) return

      detach()
      nodeRef.current = node
      if (!node) return

      reversedRef.current =
        getComputedStyle(node).flexDirection === 'column-reverse'
      node.addEventListener('scroll', schedule, { passive: true })

      resizeRef.current = new ResizeObserver(schedule)
      resizeRef.current.observe(node)

      mutationRef.current = new MutationObserver(schedule)
      mutationRef.current.observe(node, {
        childList: true,
        subtree: true,
        characterData: true
      })

      refresh()
    },
    [detach, schedule, refresh]
  )

  useEffect(() => detach, [detach])

  return { ref, className: styles.fade }
}
